import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Fetch from '../components/Fetch';
import { Share2 } from 'lucide-react';

const DetailArticlePage = () => {
  const { slug } = useParams();
  const location = useLocation();

  // Get video embed function
  const getVideoEmbed = (article) => {
    // YouTube embed
    if (article.video?.id) {
      return (
        <>
          <div className="video-embed mb-6">
            <iframe
              title="Embedded content"
              width="100%"
              height="450"
              src={`https://www.youtube.com/embed/${article.video.id}`}
              frameBorder="0"
              allowFullScreen
            ></iframe>
          </div>
          <a 
            href={`https://youtube.com/watch?v=${article.video.id}`} 
            className="video-embed-link text-xs text-gray-500 block mt-2" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Watch on YouTube
          </a>
        </>
      );
    }
    // TikTok embed
    if (article.tt_link) {
      return (
        <div className="video-embed mb-6">
          <blockquote className="tiktok-embed" cite={article.tt_link} data-video-id={article.tt_link.split('/video/')[1]?.split('?')[0]}>
            <section><a target="_blank" rel="noreferrer" href={`https://www.tiktok.com/@${article.tt_link.split('@')[1]?.split('/')[0]}`}>@{article.tt_link.split('@')[1]?.split('/')[0]}</a></section>
          </blockquote>
          <a href={article.tt_link} className="video-embed-link text-xs text-gray-500 block mt-2" target="_blank" rel="noopener noreferrer">
            Watch on TikTok
          </a>
        </div>
      );
    }
    // Instagram embed
    if (article.ig_embed_code) {
      return (
        <div className="video-embed mb-6">
          <div dangerouslySetInnerHTML={{ __html: article.ig_embed_code }} />
          <a href={article.ig_link} className="video-embed-link text-xs text-gray-500 block mt-2" target="_blank" rel="noopener noreferrer">
            View on Instagram
          </a>
        </div>
      );
    }
    return null;
  };

  // Get thumbnail function and remove the first image from article_content
  const getThumbnailAndCleanContent = (article) => {
    if (!article.article_content) return { thumbnail: null, cleanedContent: article.article_content };
    const imgRegex = /<img[^>]+src="([^">]+)"[^>]*>/;
    const match = article.article_content.match(imgRegex);
    const thumbnail = match ? match[1] : null;
    const cleanedContent = article.article_content.replace(imgRegex, '');
    return { thumbnail, cleanedContent };
  };

  // Render article content with consistent layout
  const renderArticleContent = (article) => {
    const { thumbnail, cleanedContent } = getThumbnailAndCleanContent(article);
    const hasVideo = article.video?.id || article.tt_link || article.ig_embed_code;
    
    return (
      <div className="p-4 max-w-4xl mx-auto main-detail">
        {/* Video content first if available */}
        {getVideoEmbed(article)}
        
        {/* For non-video content, show thumbnail first then title */}
        {!hasVideo && thumbnail && (
          <img 
            src={thumbnail} 
            alt="Article thumbnail" 
            className="w-full h-auto mb-6 rounded-lg"
          />
        )}
        
        {/* Title always after media for non-video content */}
        <h1 className="text-3xl font-bold mb-4">{article.article_title || 'Untitled'}</h1>
        
        {/* Article content */}
        <div 
          className="prose max-w-none"
          dangerouslySetInnerHTML={{ __html: cleanedContent || ' ' }}
        />
        
        {/* Share button */}
        <div className="share-section mt-8">
          <button
            onClick={() => {
              navigator.share({
                title: article.article_title,
                url: window.location.href
              }).catch(() => {
                // Fallback if Web Share API fails
              });
            }}
            className="article-share-btn inline-flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Share <Share2 size={16} />
          </button>
        </div>
      </div>
    );
  };

  // If coming from Card click with state
  if (location.state?.article) {
    const article = location.state.article;
    return renderArticleContent(article);
  }

  // If direct URL access, fetch the data
  return (
    <Fetch>
      {({ data, loading, error }) => {
        if (loading) return <div className="p-4">Loading article...</div>;
        if (error) return <div className="p-4">Error loading article.</div>;

        const article = data.find(item => item.slug === slug);
        if (!article) return <div className="p-4">Article not found.</div>;

        return renderArticleContent(article);
      }}
    </Fetch>
  );
};

export default DetailArticlePage;
